'use client';

import { ButtonInSection, ButtonSection, CheckboxInput, SubmitButton, Text } from '@capsicum/common-web';
import { useExportAndCloseAccountConfirmation } from '@capsicum/features/account/shared';
import { FormProvider, useForm } from 'react-hook-form';
import styled from 'styled-components';

type ExportAndCloseAccountConfirmationProps = {
	onCancel: () => void;
	onConfirm: () => Promise<void>;
};

export const ExportAndCloseAccountConfirmation = ({ onCancel, onConfirm }: ExportAndCloseAccountConfirmationProps) => {
	const { intlTexts, resolver } = useExportAndCloseAccountConfirmation();

	const formMethods = useForm({
		mode: 'onChange',
		resolver
	});

	return (
		<S.Wrapper>
			<FormProvider {...formMethods}>
				<S.Form onSubmit={formMethods.handleSubmit(onConfirm)}>
					<S.Container>
						<S.Content>
							<S.Title variant="textTopBar">{intlTexts.title}</S.Title>
							<CheckboxInput
								name="checkboxA"
								testID="settings-accountManagement-exportAndCloseAccount-confirm-checkboxA"
							>
								{intlTexts.checkboxA}
							</CheckboxInput>
							<CheckboxInput
								name="checkboxB"
								testID="settings-accountManagement-exportAndCloseAccount-confirm-checkboxB"
							>
								{intlTexts.checkboxB}
							</CheckboxInput>
							<CheckboxInput
								name="checkboxC"
								testID="settings-accountManagement-exportAndCloseAccount-confirm-checkboxC"
							>
								{intlTexts.checkboxC}
							</CheckboxInput>
							<CheckboxInput
								name="checkboxD"
								testID="settings-accountManagement-exportAndCloseAccount-confirm-checkboxD"
							>
								{intlTexts.checkboxD}
							</CheckboxInput>
							<CheckboxInput
								name="checkboxE"
								testID="settings-accountManagement-exportAndCloseAccount-confirm-checkboxE"
							>
								{intlTexts.checkboxE}
							</CheckboxInput>
						</S.Content>
					</S.Container>
					<ButtonSection $stackButtonsVertically>
						<ButtonInSection
							as={SubmitButton}
							color="destructive"
							testID="settings-accountManagement-exportAndCloseAccount-button"
							text={intlTexts.confirmButton}
						/>
						<ButtonInSection
							color="brand"
							testID="settings-accountManagement-keepAccount-button"
							text={intlTexts.cancel}
							type="button"
							variant="outlined"
							onClick={onCancel}
						/>
					</ButtonSection>
				</S.Form>
			</FormProvider>
		</S.Wrapper>
	);
};

namespace S {
	export const Wrapper = styled.div`
		display: flex;
		flex: 1;
		flex-direction: column;
		padding: ${({ theme }) => `${theme.spacings.medium} 0`};
		gap: ${({ theme }) => theme.spacings.medium};

		${({ theme }) => theme.breakpoints.queries.below.tabletSmallDesktop} {
			padding: ${({ theme }) => `${theme.spacings.medium} ${theme.spacings.small}`};
		}
	`;

	export const Form = styled.form`
		display: contents;
	`;
	export const Container = styled.div`
		display: flex;
		flex-direction: column;
		padding: ${({ theme }) => theme.spacings.small};
		gap: ${({ theme }) => theme.spacings.small};
		background-color: ${({ theme }) => theme.colors.backgrounds.main.container};
		color: ${({ theme }) => theme.colors.text.main.enabled};
		${({ theme }) => theme.border.radius.medium}
	`;
	export const Content = styled.div`
		flex-direction: column;
		display: flex;
		gap: ${({ theme }) => theme.spacings.large};
		${({ theme }) => theme.breakpoints.queries.below.mobile} {
			padding-bottom: ${({ theme }) => theme.spacings.xxxxlarge};
		}
	`;

	export const Title = styled(Text)`
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const Bold = styled.span`
		${({ theme }) => theme.fonts.text.text1}
	`;
}
